import * as d3 from 'd3';
import { number_growth_float } from '../animation/number.js';

/**
 * Generates a pie chart using D3.js.
 *
 * @param {Array} data - The data array where each element is an object containing 'count' and 'display_name' properties.
 * @param {string} id - The ID of the HTML element where the chart will be appended.
 * @param {string} text_class - The class of the HTML element where the selected arc's information will be displayed.
 * @returns {SVGElement} - The generated SVG element containing the pie chart.
 */
export async function pie_chart(data, id, text_class) { 
    const width = 320;
    const height = 320;

    const svg = d3.select(`#${id}`)
        .append('svg')
        .attr('width', '100%')
        .attr('height', height)

    const g = svg
        .append('g')
        .attr('transform', `translate(${width / 2}, ${height / 2})`);

    const pie = d3.pie().value((d) => d.count);
    const arcs = pie(data);

    const colorScale = d3.scaleOrdinal(d3.schemeSet3);
    
    const arcGenerator = d3.arc()
        .innerRadius(Math.min(width, height) / 2.3) 
        .outerRadius(Math.min(width, height) / 3.6);

    const arcHoverGenerator = d3.arc()
        .innerRadius(Math.min(width, height) / 2.2)
        .outerRadius(Math.min(width, height) / 3.4);

    const arcClickGenerator = d3.arc()
        .innerRadius(Math.min(width, height) / 2.1)
        .outerRadius(Math.min(width, height) / 3.1);

    const tooltip = d3.select(`#${id}`)
        .append('div')
        .style('position', 'absolute')
        .style('font-size', '12px')
        .style('opacity', 0) // Initially hidden
        .style('background', 'rgba(0, 0, 0, 0.6)')
        .style('color', '#fff')
        .style('border-radius', '5px')
        .style('padding', '5px');

    
    const total = d3.sum(data, d => d.count); // Calculate the total count

    const central_text = g.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.5em')
        .style('font-size', '20px')
        .style('opacity', 0); // Initially hidden

    const bottom_name = d3.select(text_class);

    let selectedArc = null;
    let click_flag = 1;

    g.selectAll('path')
        .data(arcs)
        .enter()
        .append('path')
        .attr('fill', (d) => {
            if (d.data.name === 'others') {
                return 'Gainsboro';
            } else {
                return colorScale(d.index);
            }
        })
        // Mouse Hover: Initial rendering
        .attr('d', arcGenerator)
        .on('mouseover', function(event, d) {
            if (selectedArc !== this) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('d', arcHoverGenerator(d));
            }

            tooltip.style("opacity", 1);
        })
        .on('mousemove', function(event, d) {
            let pt = d3.pointer(event, this)
            tooltip.style("opacity", 1)
                    .text(d.data.display_name)
                    .style('left', pt[0] + 200 + 'px')
                    .style('top', pt[1] + 170 + 'px');

        })
        .on('click', function(event, d) {
            // If there is no previously selected arc, select the current one
            if (!selectedArc | click_flag === 1) {
                selectedArc = this; // first select
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('d', arcClickGenerator(d))
                    .attr('stroke', 'white')
                    .attr('stroke-width', '2.5px');

                click_flag = 0;
            } else {
                // If a different arc is clicked, update the selection
                if (selectedArc !== this) {
                    d3.select(selectedArc)
                        .transition()
                        .duration(200)
                        .attr('d', arcGenerator(selectedArc.__data__))
                        .attr('stroke-width', '0px');

                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr('d', arcClickGenerator(d))
                        .attr('stroke', 'white')
                        .attr('stroke-width', '3px');

                    selectedArc = this;
                } else {
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr('d', arcGenerator(d))
                        .attr('stroke-width', '0px');
                    click_flag = 1;
                }
            }

            // Update the text with count / total
            central_text.html(`
                <tspan style="fill: rgba(0,194,165,1); font: bold 35px sans-serif;" class = "data_count">${d.data.count}</tspan>
                <tspan style="fill: DarkGray; font: bold 20px sans-serif;">/${total}</tspan>
            `).style('opacity', 1); // Show the text

            // Update the bottom text with display_name
            bottom_name.html(`
                <p class="card-sm-num text-center">${d.data.display_name}</p>
            `);

            if (d.data.zh_tw_label && d.data.zh_tw_label.value !== undefined) {
                bottom_name.append('p')
                    .attr('class', 'card-sm-num text-center')
                    .text(d.data.zh_tw_label.value);
            }
        })
        .on('mouseout', function(event, d) {
            if (selectedArc !== this) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('d', arcGenerator(d));
            }
    
            tooltip.style("opacity", 0);
        })
        
        // init animation
        .transition() 
        .duration(1000)
        .attrTween("d", function(d) {
            let d3Interpolate = d3.interpolate({startAngle: 0, endAngle: 0}, d);
            return function(t) {
                return arcGenerator(d3Interpolate(t));  
            };
        });

    return svg.node();
}

/**
 * Updates or creates a pie chart using D3.js.
 *
 * @param {Array} data - The data array where each element is an object containing 'count' and 'display_name' properties.
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {string} text_id - The ID of the HTML element where the selected arc's information will be displayed.
 * @returns {SVGElement} - The generated or updated SVG element containing the pie chart.
 */
export function update_pie_chart(data, container_id, text_id) {
    const svg = d3.select(`#${container_id} svg`);
    // Create
    if (svg.empty()) {
        return pie_chart(data, container_id, text_id);
    }
    // Update 
    else {
        svg.remove();
        return pie_chart(data, container_id, text_id);
    }
}

/**
 * Generates a simple pie chart using D3.js.
 *
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {Object} data - The data object containing 'value' property.
 * @returns {Object} - The generated SVG node.
 */
export function simple_pie_chart(container_id, data) { 
    const width = 300;
    const height = Math.min(width, 500);
    const radius = Math.min(width, height) / 2;

    const ratio = (data['value'] * 100).toFixed(0);

    const arc_background = d3.arc()
        .innerRadius(radius * 0.8)
        .outerRadius(radius - 1)
        .startAngle(0)
        .endAngle(2*Math.PI);

    const arc = d3.arc()
        .innerRadius(radius * 0.8)
        .outerRadius(radius - 1)
        .startAngle(0)
        .endAngle(ratio/100 * Math.PI * 2)
        .cornerRadius(20);

    const pie = d3.pie()
        .sort(null)
        .value(d => d.value);

    const color = ["rgba(0,194,165,1)", "Gainsboro"];

    const svg = d3.select(`#${container_id}`)
        .append('svg')
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [-width / 2, -height / 2, width, height])
        .attr("style", "max-width: 100%; height: auto;");

    const g = svg.append("g");

    let path_background = g.append("path")
        .attr("fill", color[1])
        .attr("d", arc_background);

    let path = g.append("path")
        .attr("fill", (d) => {
            if(data.value >= 0.6) {
                return color[0];
            } else if (data.value >= 0.3) {
                return "gold";
            } else {
                return "salmon";
            }
        })                
        .attr("d", arc);

    let middleCount = g.append("text")
        .text(ratio + "%")
        .attr("text-anchor", "middle")
        .attr("dy", "0.3em")
        // .attr("fill", "rgba(0,194,165,1)")
        .attr("fill", (d) => {
            if(data.value >= 0.6) {
                return color[0];
            } else if (data.value >= 0.3) {
                return "orange";
            } else {
                return "salmon";
            }
        })  
        .style("font-size", "50px")
        .style("font-weight", "bold");

    return svg.node();
}